import type { DeviceResponseV5 } from '@emma/api_client/types';
import {
  Box,
  Button,
  Checkbox,
  CheckboxGroup,
  Flex,
  Icon,
  Input,
  Popover,
  Portal,
  Text,
  Tooltip,
} from '@lego/klik-ui';
import { PlusBold } from '@lego/klik-ui/icons';
import { type MouseEvent, useMemo, useState } from 'react';
import { Loader } from './Loader';

type DeviceAssignPopoverProps = {
  assignableDevices: DeviceResponseV5[];
  isLoadingDevices: boolean;
  hasError?: boolean;
  onAssign: (deviceIds: number[]) => Promise<{ failedDeviceIds: number[] } | null>;
  tooltipLabel: string;
};

const DeviceList = ({
  assignableDevices,
  filteredDevices,
  selectedDeviceIds,
  onToggle,
  isLoadingDevices,
  hasError,
}: {
  assignableDevices: DeviceResponseV5[];
  filteredDevices: DeviceResponseV5[];
  selectedDeviceIds: Set<number>;
  onToggle: (id: number) => void;
  isLoadingDevices: boolean;
  hasError?: boolean;
}) => {
  if (isLoadingDevices) return <Loader />;
  if (hasError)
    return (
      <Text color="red.500" fontSize="sm">
        Unable to load workspace devices.
      </Text>
    );
  if (assignableDevices.length === 0)
    return (
      <Text color="gray.500" fontSize="sm">
        No devices available to assign.
      </Text>
    );
  if (filteredDevices.length === 0)
    return (
      <Text color="gray.500" fontSize="sm">
        No devices match your search.
      </Text>
    );

  return (
    <CheckboxGroup>
      {filteredDevices.map((device) => (
        <Flex key={device.id} p={1}>
          <Checkbox
            isChecked={selectedDeviceIds.has(device.id)}
            onChange={() => onToggle(device.id)}
          >
            <Text fontSize="sm">
              {device.name} / Device #{device.id}
            </Text>
          </Checkbox>
        </Flex>
      ))}
    </CheckboxGroup>
  );
};

export const DeviceAssignPopover = ({
  assignableDevices,
  isLoadingDevices,
  hasError,
  onAssign,
  tooltipLabel,
}: DeviceAssignPopoverProps) => {
  const [selectedDeviceIds, setSelectedDeviceIds] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isAssigning, setIsAssigning] = useState(false);

  const filteredDevices = useMemo(() => {
    if (!searchQuery.trim()) return assignableDevices;
    const query = searchQuery.toLowerCase();
    return assignableDevices.filter(
      (d) => d.name.toLowerCase().includes(query) || String(d.id).includes(query)
    );
  }, [assignableDevices, searchQuery]);

  const handleToggleDevice = (deviceId: number) => {
    setSelectedDeviceIds((prev) => {
      const next = new Set(prev);
      if (next.has(deviceId)) {
        next.delete(deviceId);
      } else {
        next.add(deviceId);
      }
      return next;
    });
  };

  const handleAssign = async (e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (selectedDeviceIds.size === 0) return;

    setIsAssigning(true);
    const result = await onAssign([...selectedDeviceIds]);
    setIsAssigning(false);

    if (result?.failedDeviceIds.length) {
      setSelectedDeviceIds(new Set(result.failedDeviceIds));
    } else if (result) {
      setSelectedDeviceIds(new Set());
      setSearchQuery('');
      setIsOpen(false);
    }
  };

  const handleClose = () => {
    setSelectedDeviceIds(new Set());
    setSearchQuery('');
    setIsOpen(false);
  };

  return (
    <Popover isLazy isOpen={isOpen} onClose={handleClose} placement="bottom-end">
      <Popover.Trigger>
        <Tooltip label={tooltipLabel} placement="left">
          <Button
            borderRightRadius="0px"
            leftIcon={<Icon as={PlusBold} />}
            onClick={(e: MouseEvent) => {
              e.preventDefault();
              e.stopPropagation();
              setIsOpen((prev) => !prev);
            }}
            size="sm"
            variant="outline"
          >
            Assign
          </Button>
        </Tooltip>
      </Popover.Trigger>
      <Portal>
        <Popover.Content onClick={(e: MouseEvent) => e.stopPropagation()} p={4} width="320px">
          <Popover.Arrow />
          <Popover.Body p={0}>
            <Input
              autoFocus
              mb={2}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search devices..."
              size="sm"
              value={searchQuery}
            />
            <Box maxH="250px" overflowY="auto">
              <DeviceList
                assignableDevices={assignableDevices}
                filteredDevices={filteredDevices}
                hasError={hasError}
                isLoadingDevices={isLoadingDevices}
                onToggle={handleToggleDevice}
                selectedDeviceIds={selectedDeviceIds}
              />
            </Box>
            {assignableDevices.length > 0 && (
              <Button
                isDisabled={selectedDeviceIds.size === 0}
                isLoading={isAssigning}
                mt={3}
                onClick={handleAssign}
                size="sm"
                variant="primary"
                width="100%"
              >
                Assign {selectedDeviceIds.size > 0 ? `(${selectedDeviceIds.size})` : ''}
              </Button>
            )}
          </Popover.Body>
        </Popover.Content>
      </Portal>
    </Popover>
  );
};
